import { updateObjectAssetPlacementPreview } from '../objects/object_asset_system.js?v=20260903c';
// @ts-check

import { AppRuntime } from '../app/app_runtime.js?v=20260706d';
import * as SystemRegistry from '../registry/system_registry.js?v=20260904f';
import {
  getInteractionFlag,
  setInteractionFlag
} from './interaction_transient_state.js?v=20260904f';
import {
  activateDimensionTool,
  activateAngularDimensionTool,
  ANGULAR_DIMENSION_TOOL,
  deactivateDimensionTool,
  isDimensionToolActive,
  syncDimensionToolbarButton,
  cancelDimensionInteraction
} from '../services/dimension_service.js?v=20260904f';
import {
  activateTextAnnotationTool,
  cancelTextAnnotationInteraction,
  deactivateTextAnnotationTool,
  isTextAnnotationToolActive,
  syncTextAnnotationToolbarButton,
  TEXT_ANNOTATION_TOOL
} from '../services/text_annotation_service.js?v=20260903c';
import {
  activateAreaMeasurementTool,
  cancelAreaMeasurementInteraction,
  deactivateAreaMeasurementTool,
  isAreaMeasurementToolActive,
  syncAreaMeasurementToolbarButton,
  AREA_MEASUREMENT_TOOL
} from '../services/area_measurement_service.js?v=20260903c';
import {
  activateAutoAreaMeasurementTool,
  cancelAutoAreaMeasurementInteraction,
  deactivateAutoAreaMeasurementTool,
  isAutoAreaMeasurementToolActive,
  syncAutoAreaMeasurementToolbarButton,
  AUTO_AREA_MEASUREMENT_TOOL
} from '../services/auto_area_measurement_service.js?v=20260814c';
import {
  activateObstacleTool,
  finishObstacleDraft,
  finishObstacleMoveDrag,
  finishObstacleRotateDrag,
  hitTestObstacle,
  obstacleActiveTool,
  resetObstacleDrawState,
  selectObstacle,
  setObstacleTool,
  startObstacleMoveDrag,
  syncObstacleToolbarButtons,
  updateGlobalObstacleDrawPreview,
  updateObstacleDraft,
  updateObstacleMoveDrag,
  updateObstacleRotateDrag
} from '../obstacles/obstacles.js?v=20260903c';
import {
  activateObjectLibraryPanel,
  closeObjectLibraryPanel,
  objectLibraryPanelActive,
  OBJECT_LIBRARY_TOOL,
  syncObjectLibraryToolbarButton
} from '../objects/object_library.js?v=20260706d';
import { S } from '../app/app_state.js?v=20260817c';

const CORE_TOOL_OWNER = 'core';

function isPlainToolMetaRecord(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  try {
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
  } catch {
    return false;
  }
}

function cloneToolMetaValue(value, ancestors = new WeakSet(), depth = 0) {
  if (value === null || ['string', 'boolean', 'undefined'].includes(typeof value)) return value;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (depth > 12 || (typeof value !== 'object' && typeof value !== 'function')) return null;
  if (ancestors.has(value)) return null;
  ancestors.add(value);
  try {
    if (Array.isArray(value)) {
      return Object.freeze(value.map(entry => cloneToolMetaValue(entry, ancestors, depth + 1)));
    }
    if (!isPlainToolMetaRecord(value)) return null;
    const clone = {};
    for (const [key, entry] of Object.entries(value)) {
      clone[key] = cloneToolMetaValue(entry, ancestors, depth + 1);
    }
    return Object.freeze(clone);
  } catch {
    return null;
  } finally {
    ancestors.delete(value);
  }
}

function normalizeToolMeta(meta) {
  const clone = cloneToolMetaValue(meta);
  return isPlainToolMetaRecord(clone) ? clone : Object.freeze({});
}

function isAllowedToolOwner(owner) {
  if (owner === CORE_TOOL_OWNER) return true;
  return Boolean(owner && SystemRegistry.getSystem(owner));
}

/**
 * Stellt sicher ensure State im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt State-Normalisierung, Migration und robuste Defaults, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function ensureState() {
  if (!S.ui || typeof S.ui !== 'object') S.ui = {};
  if (S.ui.activeTool && typeof S.ui.activeTool !== 'object') S.ui.activeTool = null;
  return S.ui;
}

/**
 * Normalisiert normalize Tool im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt Toolbar-, Werkzeug- und Bedienzustände, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} tool - Eingabewert für normalize Tool.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function normalizeTool(tool) {
  try {
    if (!tool) return null;
    if (typeof tool === 'string') {
      const toolId = tool.trim();
      return toolId
        ? Object.freeze({ owner: CORE_TOOL_OWNER, tool: toolId, meta: Object.freeze({}) })
        : null;
    }
    if (typeof tool !== 'object' || Array.isArray(tool)) return null;
    const owner = String(tool.owner || '').trim();
    const toolId = String(tool.tool || '').trim();
    if (!owner || !toolId) return null;
    return Object.freeze({
      owner,
      tool: toolId,
      meta: normalizeToolMeta(tool.meta)
    });
  } catch {
    return null;
  }
}

function activeToolState(ui) {
  if (!ui?.activeTool) return null;
  const normalized = normalizeTool(ui.activeTool);
  if (!normalized || !isAllowedToolOwner(normalized.owner)) {
    ui.activeTool = null;
    return null;
  }
  ui.activeTool = normalized;
  return normalized;
}

function readonlyToolSnapshot(tool) {
  const normalized = normalizeTool(tool);
  if (!normalized || !isAllowedToolOwner(normalized.owner)) return null;
  return Object.freeze({
    owner: normalized.owner,
    tool: normalized.tool,
    meta: normalizeToolMeta(normalized.meta)
  });
}

/**
 * Beschreibt active Tool im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt Toolbar-, Werkzeug- und Bedienzustände, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function activeTool() {
  const ui = ensureState();
  return readonlyToolSnapshot(activeToolState(ui));
}

/**
 * Prüft is Active im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} owner - Eingabewert für is Active.
 * @param {*} tool - Eingabewert für is Active.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function isActive(owner, tool) {
  const active = activeTool();
  return Boolean(active) && active.owner === owner && active.tool === tool;
}

/**
 * Aktualisiert set Active Tool im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt Toolbar-, Werkzeug- und Bedienzustände, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} tool - Eingabewert für set Active Tool.
 * @param {*} options - Optionale Steuerwerte, die Defaults oder Verhalten überlagern.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function setActiveTool(tool, options = {}) {
  const ui = ensureState();
  if (!ui) return null;
  const next = normalizeTool(tool);
  if (tool != null && !next) return null;
  if (next && !isAllowedToolOwner(next.owner)) return null;
  if (!(next?.owner === CORE_TOOL_OWNER && next?.tool === OBJECT_LIBRARY_TOOL)) {
    closeObjectLibraryPanel();
  }
  const previous = activeToolState(ui);
  if (
    options.toggle === true &&
    previous &&
    next &&
    previous.owner === next.owner &&
    previous.tool === next.tool
  ) {
    return clearActiveTool();
  }
  let closedLevelsPanel = false;
  if (!(next?.owner === CORE_TOOL_OWNER && next?.tool === 'levels')) {
    closedLevelsPanel = AppRuntime.closeLevelsPanelForToolChange({ keepActiveTool: true });
  }
  if (previous?.owner === CORE_TOOL_OWNER && ['dimension', ANGULAR_DIMENSION_TOOL].includes(previous.tool) && !(next?.owner === CORE_TOOL_OWNER && next.tool === previous.tool)) {
    deactivateDimensionTool();
  }
  if (previous?.owner === CORE_TOOL_OWNER && previous.tool === TEXT_ANNOTATION_TOOL && !(next?.owner === CORE_TOOL_OWNER && next.tool === TEXT_ANNOTATION_TOOL)) {
    deactivateTextAnnotationTool();
  }
  if (previous?.owner === CORE_TOOL_OWNER && previous.tool === AREA_MEASUREMENT_TOOL && !(next?.owner === CORE_TOOL_OWNER && next.tool === AREA_MEASUREMENT_TOOL)) {
    deactivateAreaMeasurementTool();
  }
  if (previous?.owner === CORE_TOOL_OWNER && previous.tool === AUTO_AREA_MEASUREMENT_TOOL && !(next?.owner === CORE_TOOL_OWNER && next.tool === AUTO_AREA_MEASUREMENT_TOOL)) {
    deactivateAutoAreaMeasurementTool();
  }
  ui.activeTool = next;
  if (next) {
    if (next.owner !== CORE_TOOL_OWNER) {
      SystemRegistry.activateSystem(next.owner);
    }
  }
  syncToolbarState();
  if (closedLevelsPanel) {
    AppRuntime.requestAppRefresh(false, 'levels-panel-tool-change');
  }
  return readonlyToolSnapshot(next);
}

/**
 * Entfernt clear Active Tool im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt Toolbar-, Werkzeug- und Bedienzustände, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} owner - Eingabewert für clear Active Tool.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function clearActiveTool(owner = null) {
  const ui = ensureState();
  if (!ui) return null;
  const previous = activeToolState(ui);
  let requestedOwner;
  try {
    requestedOwner = owner == null ? '' : String(owner).trim();
  } catch {
    return readonlyToolSnapshot(previous);
  }
  if (requestedOwner && !isAllowedToolOwner(requestedOwner)) return readonlyToolSnapshot(previous);
  if (requestedOwner && previous?.owner !== requestedOwner) return readonlyToolSnapshot(previous);
  ui.activeTool = null;
  if (previous?.owner === CORE_TOOL_OWNER && previous.tool === 'levels') {
    AppRuntime.closeLevelsPanelForToolChange({ keepActiveTool: true });
  }
  if (previous?.owner === CORE_TOOL_OWNER && ['dimension', ANGULAR_DIMENSION_TOOL].includes(previous.tool)) {
    deactivateDimensionTool();
  }
  if (previous?.owner === CORE_TOOL_OWNER && previous.tool === TEXT_ANNOTATION_TOOL) {
    deactivateTextAnnotationTool();
  }
  if (previous?.owner === CORE_TOOL_OWNER && previous.tool === AREA_MEASUREMENT_TOOL) {
    deactivateAreaMeasurementTool();
  }
  if (previous?.owner === CORE_TOOL_OWNER && previous.tool === AUTO_AREA_MEASUREMENT_TOOL) {
    deactivateAutoAreaMeasurementTool();
  }
  syncToolbarState();
  return null;
}

/**
 * Verarbeitet handle Tool Action im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt Toolbar-, Werkzeug- und Bedienzustände, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} owner - Eingabewert für handle Tool Action.
 * @param {*} tool - Eingabewert für handle Tool Action.
 * @param {*} payload - Eingabewert für handle Tool Action.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function handleToolAction(owner, tool, payload = null) {
  let toolOwner;
  let toolId;
  try {
    toolOwner = owner == null || owner === '' ? CORE_TOOL_OWNER : String(owner).trim();
    toolId = String(tool || '').trim();
  } catch {
    return false;
  }
  if (!toolOwner || !toolId || !isAllowedToolOwner(toolOwner)) return false;
  const isLevelsTool = toolOwner === CORE_TOOL_OWNER && (toolId === 'levels' || toolId === 'toggle_levels');
  const closedLevelsPanel = !isLevelsTool
    ? AppRuntime.closeLevelsPanelForToolChange()
    : false;
  if (toolOwner === CORE_TOOL_OWNER && handleCoreToolAction(toolId, payload)) {
    if (closedLevelsPanel) AppRuntime.requestAppRefresh(false, 'levels-panel-core-tool-action');
    return true;
  }
  if (toolOwner !== CORE_TOOL_OWNER) {
    const handled = SystemRegistry.handleToolAction(toolOwner, toolId, payload) === true;
    if (handled && closedLevelsPanel) AppRuntime.requestAppRefresh(false, 'levels-panel-system-tool-action');
    return handled;
  }
  setActiveTool({ owner: CORE_TOOL_OWNER, tool: toolId }, { toggle: true });
  if (closedLevelsPanel) AppRuntime.requestAppRefresh(false, 'levels-panel-generic-tool-action');
  return true;
}

/**
 * Verarbeitet handle Core Tool Action im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt Toolbar-, Werkzeug- und Bedienzustände, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} tool - Eingabewert für handle Core Tool Action.
 * @param {*} payload - Eingabewert für handle Core Tool Action.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function handleCoreToolAction(tool, payload = null) {
  if (tool === OBJECT_LIBRARY_TOOL || tool === 'objects' || tool === 'object_selection') {
    if (isAreaMeasurementToolActive()) deactivateAreaMeasurementTool();
    if (isAutoAreaMeasurementToolActive()) deactivateAutoAreaMeasurementTool();
    if (!objectLibraryPanelActive()) {
      prepareExclusivePlanToolActivation('object-library');
      setActiveTool({ owner: CORE_TOOL_OWNER, tool: OBJECT_LIBRARY_TOOL });
    } else {
      clearActiveTool(CORE_TOOL_OWNER);
    }
    activateObjectLibraryPanel(payload || {});
    return true;
  }
  closeObjectLibraryPanel();
  if (tool === 'levels' || tool === 'toggle_levels') {
    if (isAreaMeasurementToolActive()) deactivateAreaMeasurementTool();
    if (isAutoAreaMeasurementToolActive()) deactivateAutoAreaMeasurementTool();
    AppRuntime.toggleLevelsTool();
    return true;
  }
  if (tool === 'dimension' || tool === 'dimension_tool' || tool === ANGULAR_DIMENSION_TOOL) {
    const requestedTool = tool === ANGULAR_DIMENSION_TOOL ? ANGULAR_DIMENSION_TOOL : 'dimension';
    if (isDimensionToolActive() && S.ui.activeTool.tool === requestedTool) {
      deactivateDimensionTool();
    } else {
      cancelDimensionInteraction();
      cancelTextAnnotationInteraction();
      cancelAreaMeasurementInteraction();
      cancelAutoAreaMeasurementInteraction();
      prepareExclusivePlanToolActivation('dimension-tool');
      if (requestedTool === ANGULAR_DIMENSION_TOOL) activateAngularDimensionTool();
      else activateDimensionTool();
    }
    AppRuntime.requestAppRefresh(false, 'dimension-tool');
    return true;
  }
  if (tool === TEXT_ANNOTATION_TOOL || tool === 'text' || tool === 'text_annotation_tool') {
    if (isTextAnnotationToolActive()) {
      deactivateTextAnnotationTool();
    } else {
      cancelDimensionInteraction();
      cancelAreaMeasurementInteraction();
      cancelAutoAreaMeasurementInteraction();
      prepareExclusivePlanToolActivation('text-annotation-tool');
      activateTextAnnotationTool();
    }
    AppRuntime.requestAppRefresh(false, 'text-annotation-tool');
    return true;
  }
  if (tool === AREA_MEASUREMENT_TOOL || tool === 'area' || tool === 'area_measurement_tool') {
    if (isAreaMeasurementToolActive()) {
      deactivateAreaMeasurementTool();
    } else {
      cancelDimensionInteraction();
      cancelTextAnnotationInteraction();
      cancelAreaMeasurementInteraction();
      cancelAutoAreaMeasurementInteraction();
      prepareExclusivePlanToolActivation('area-measurement-tool');
      activateAreaMeasurementTool();
    }
    AppRuntime.requestAppRefresh(false, 'area-measurement-tool');
    return true;
  }
  if (tool === AUTO_AREA_MEASUREMENT_TOOL || tool === 'auto_area' || tool === 'automatic_area_measurement') {
    if (isAutoAreaMeasurementToolActive()) {
      deactivateAutoAreaMeasurementTool();
    } else {
      cancelDimensionInteraction();
      cancelTextAnnotationInteraction();
      cancelAreaMeasurementInteraction();
      cancelAutoAreaMeasurementInteraction();
      prepareExclusivePlanToolActivation('auto-area-measurement-tool');
      activateAutoAreaMeasurementTool();
    }
    AppRuntime.requestAppRefresh(false, 'auto-area-measurement-tool');
    return true;
  }
  if (tool === 'obstacle_pipe' || tool === 'pipe') {
    if (isAreaMeasurementToolActive()) deactivateAreaMeasurementTool();
    if (isAutoAreaMeasurementToolActive()) deactivateAutoAreaMeasurementTool();
    activateObstacleTool('pipe', payload || {});
    return true;
  }
  if (tool === 'obstacle_duct' || tool === 'duct') {
    if (isAreaMeasurementToolActive()) deactivateAreaMeasurementTool();
    if (isAutoAreaMeasurementToolActive()) deactivateAutoAreaMeasurementTool();
    activateObstacleTool('duct', payload || {});
    return true;
  }
  return false;
}

function prepareExclusivePlanToolActivation(reason = 'plan-tool') {
  const context = { currentView: S.project?.currentView || 'plan', reason };
  resetObstacleDrawState();
  setObstacleTool(null);
  SystemRegistry.cancelInteractions(context);
  AppRuntime.clearGlobalSelection();
}

/**
 * Verarbeitet handle Pointer Down im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} event - Browser-/Pointer-Event aus der Bedienoberfläche.
 * @param {*} context - Laufzeitkontext mit View-, Pointer- oder Renderingdaten.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function handlePointerDown(event, context = {}) {
  if (context.currentView && context.currentView !== 'plan') return false;
  if (
    obstacleActiveTool()
    && isPlainPrimaryPointer(event)
  ) {
    event.stopPropagation();
    return true;
  }
  const obstacleHit = hitTestObstacle(event.clientX, event.clientY);
  if (obstacleHit) {
    if (event.ctrlKey || event.metaKey || event.shiftKey) {
      event.preventDefault();
      event.stopPropagation();
      selectObstacle(obstacleHit.obstacle.id, { toggle: true });
      setInteractionFlag('suppressPlanClick', true);
      AppRuntime.refresh(false);
      return true;
    }
    return startObstacleMoveDrag(event, obstacleHit.obstacle) === true;
  }
  return false;
}

/**
 * Verarbeitet handle Pointer Move im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} event - Browser-/Pointer-Event aus der Bedienoberfläche.
 * @param {*} context - Laufzeitkontext mit View-, Pointer- oder Renderingdaten.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function handlePointerMove(event, context = {}) {
  if (context.currentView && context.currentView !== 'plan') return false;
  if (updateObjectAssetPlacementPreview(event)) return true;
  const drag = S.drag;
  const active = activeTool();
  const activePointerTool = Boolean(active) && active.owner !== 'core';
  if (obstacleActiveTool()) {
    return updateGlobalObstacleDrawPreview(event) === true;
  }
  if (drag?.type === 'obstacleDraft') {
    return updateObstacleDraft(event) === true;
  }
  if (drag?.type === 'obstacleMove') {
    return updateObstacleMoveDrag(event) === true;
  }
  if (drag?.type === 'obstacleRotate') {
    return updateObstacleRotateDrag(event) === true;
  }
  if (!drag?.active && !activePointerTool) {
    if (SystemRegistry.wantsPointerMoveFrame({ event, ...context }) !== true) return false;
  }
  // Ein laufender Drag, der von einem NIE "aktiven" System gestartet wurde
  // (z.B. core_object_assets — hiddenFromNavigation/globalSystem, kann nie
  // activeSystem() sein), würde sonst hier ohne systemId ankommen und
  // SystemRegistry.handlePointerMove() auf das gerade navigierte Produkt-
  // system statt auf den tatsächlichen Drag-Besitzer routen (das Objekt
  // folgt dann nie der Maus). Der Aufrufer übergibt heute nie ein eigenes
  // context.systemId, daher ist das rein additiv für jeden anderen Fall.
  const routedSystemId = context.systemId || drag?.systemId || null;
  return SystemRegistry.handlePointerMove(event, routedSystemId ? { ...context, systemId: routedSystemId } : context) === true;
}

/**
 * Verarbeitet handle Pointer Up im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} event - Browser-/Pointer-Event aus der Bedienoberfläche.
 * @param {*} context - Laufzeitkontext mit View-, Pointer- oder Renderingdaten.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function handlePointerUp(event, context = {}) {
  if (context.currentView && context.currentView !== 'plan') return false;
  const drag = S.drag;
  if (drag?.type === 'obstacleDraft') {
    if (event?.type === 'blur') {
      S.drag = { active: false, type: null };
      AppRuntime.requestPlanRender('obstacle-draft-focus-loss');
      return true;
    }
    return finishObstacleDraft(event) === true;
  }
  if (drag?.type === 'obstacleMove') {
    return finishObstacleMoveDrag(event) === true;
  }
  if (drag?.type === 'obstacleRotate') {
    return finishObstacleRotateDrag(event) === true;
  }
  // Siehe Kommentar in handlePointerMove() oben — derselbe Routing-Fix für
  // pointerup.
  const routedSystemId = context.systemId || drag?.systemId || null;
  return SystemRegistry.handlePointerUp(event, routedSystemId ? { ...context, systemId: routedSystemId } : context) === true;
}

/**
 * Aktualisiert set Legacy Active Tool im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt Toolbar-, Werkzeug- und Bedienzustände, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} owner - Eingabewert für set Legacy Active Tool.
 * @param {*} tool - Eingabewert für set Legacy Active Tool.
 * @param {*} meta - Eingabewert für set Legacy Active Tool.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function setLegacyActiveTool(owner, tool, meta = {}) {
  const ui = ensureState();
  if (!ui) return null;
  const previous = activeToolState(ui);
  let requestedOwner;
  const hasRequestedOwner = owner !== null && owner !== undefined && owner !== '';
  try {
    requestedOwner = hasRequestedOwner ? String(owner).trim() : CORE_TOOL_OWNER;
  } catch {
    return readonlyToolSnapshot(previous);
  }
  if (!tool || tool === 'idle') {
    if (hasRequestedOwner && (!requestedOwner || !isAllowedToolOwner(requestedOwner))) {
      return readonlyToolSnapshot(previous);
    }
    if (!hasRequestedOwner || previous?.owner === requestedOwner) ui.activeTool = null;
    return readonlyToolSnapshot(ui.activeTool);
  }
  const next = normalizeTool({ owner: requestedOwner, tool, meta });
  if (!next || !isAllowedToolOwner(next.owner)) return null;
  if (!(next.owner === CORE_TOOL_OWNER && (next.tool === 'levels' || next.tool === 'toggle_levels'))) {
    AppRuntime.closeLevelsPanelForToolChange({ keepActiveTool: true });
  }
  ui.activeTool = next;
  return readonlyToolSnapshot(next);
}

/**
 * Beschreibt sync From Legacy im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function syncFromLegacy() {
  if (S.ui?.showObjectLibraryPanel) {
    return setLegacyActiveTool('core', OBJECT_LIBRARY_TOOL);
  }
  if (S.ui?.showLevelsPanel) {
    return setLegacyActiveTool('core', 'levels');
  }
  const obstacleTool = S.ui?.obstacleTool || null;
  if (obstacleTool) {
    return setLegacyActiveTool('core', `obstacle_${obstacleTool}`, { obstacleType: obstacleTool });
  }
  if (isDimensionToolActive()) {
    return setLegacyActiveTool('core', S.ui.activeTool.tool, S.ui.activeTool.meta || {});
  }
  if (S.ui?.activeTool?.owner === 'core' && S.ui.activeTool.tool === TEXT_ANNOTATION_TOOL) {
    return setLegacyActiveTool('core', TEXT_ANNOTATION_TOOL, S.ui.activeTool.meta || {});
  }
  if (S.ui?.activeTool?.owner === 'core' && S.ui.activeTool.tool === AREA_MEASUREMENT_TOOL) {
    return setLegacyActiveTool('core', AREA_MEASUREMENT_TOOL, S.ui.activeTool.meta || {});
  }
  if (S.ui?.activeTool?.owner === 'core' && S.ui.activeTool.tool === AUTO_AREA_MEASUREMENT_TOOL) {
    return setLegacyActiveTool('core', AUTO_AREA_MEASUREMENT_TOOL, S.ui.activeTool.meta || {});
  }
  const activeSystemId = SystemRegistry.activeSystem()?.id || null;
  const systemTool = SystemRegistry.syncActiveToolFromLegacy({ activeSystemId });
  if (systemTool?.tool) {
    return setLegacyActiveTool(systemTool.owner, systemTool.tool, systemTool.meta || {});
  }
  return setLegacyActiveTool(null, null);
}

/**
 * Beschreibt sync Toolbar State im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt Toolbar-, Werkzeug- und Bedienzustände, damit Aufrufer nicht die Moduldetails kennen müssen.
 * Seiteneffekte: Beachtet Toolbar-, Werkzeug- und Bedienzustände; Änderungen erfolgen je nach Aufrufer am übergebenen Objekt oder globalen State.
 */
export function syncToolbarState() {
  syncObstacleToolbarButtons();
  syncObjectLibraryToolbarButton();
  syncDimensionToolbarButton();
  syncAreaMeasurementToolbarButton();
  syncAutoAreaMeasurementToolbarButton();
  syncTextAnnotationToolbarButton();
  SystemRegistry.renderToolbar();
}

/**
 * Prüft is Plain Primary Pointer im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} event - Browser-/Pointer-Event aus der Bedienoberfläche.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function isPlainPrimaryPointer(event) {
  return event?.button === 0
    && !event.altKey
    && !event.shiftKey
    && !(event.ctrlKey || event.metaKey)
    && !getInteractionFlag('spaceDown');
}

export function constrainPointToOrthogonal(start, point, shiftKey) {
  if (!shiftKey || !start || !point) return point;
  const dx = point.x - start.x;
  const dz = point.z - start.z;
  return Math.abs(dx) >= Math.abs(dz)
    ? { x: point.x, z: start.z }
    : { x: start.x, z: point.z };
}
